import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Tooltip } from '@mui/material';
import ModalWrapper from '../ModalWrapper';
import { useSnackbar } from '../../utils/useSnackbar';
import { submitFeedback } from '../../utils/api/feedback_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import {
  FEEDBACK_TYPE_OPTIONS,
  FEEDBACK_TYPE_SCHEMA_VALUES,
  FEEDBACK_TYPE_HELPER_TEXT,
  getCurrentRoutePath,
} from '../../utils/feedbackUtils';
import styles from './FeedbackModal.module.scss';

const feedbackSchema = z.object({
  type: z.enum(FEEDBACK_TYPE_SCHEMA_VALUES),
  description: z.string().trim().min(10, 'Description must be at least 10 characters').max(1000),
});

const MODAL_CONTENT = {
  title: 'Share Feedback',
  typeLabel: 'Feedback Type',
  descriptionLabel: 'Description',
  currentPageLabel: 'Current page',
  submitLabel: 'Submit',
  submittingLabel: 'Submitting...',
  closeLabel: 'Close',
  characterCountFormat: (n) => `${n} / 1000`,
};

const FeedbackModal = ({ open, onClose }) => {
  const { showSnackbar } = useSnackbar();
  const [currentRoute, setCurrentRoute] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentRoute(getCurrentRoutePath());
    }
  }, [open]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { type: undefined, description: '' },
  });

  const selectedType = watch('type');
  const description = watch('description') || '';

  const onSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await submitFeedback({
        type: data.type,
        description: data.description,
        current_route: currentRoute,
      });

      if (result.status === STATUS.SUCCESS) {
        showSnackbar('Feedback submitted. Thank you!', 'success');
        reset();
        onClose();
      } else {
        showSnackbar(/* result.error || */ 'Failed to submit feedback. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Use trimmed length so whitespace-only input (e.g. 10 spaces) keeps Submit disabled (QPN-2002 / BR-002).
  const isSubmitDisabled =
    !selectedType || description.trim().length < 10 || isSubmitting;

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  return (
    <ModalWrapper
      isOpen={open}
      onClose={handleClose}
      title={MODAL_CONTENT.title}
      size="medium"
      closeOnBackdrop={false}
      showCloseButton
      footer={
        <div className={styles.footer}>
          <button
            type="submit"
            form="feedback-form"
            className={styles.submitButton}
            disabled={isSubmitDisabled}
          >
            {isSubmitting ? MODAL_CONTENT.submittingLabel : MODAL_CONTENT.submitLabel}
          </button>
        </div>
      }
    >
      <form id="feedback-form" onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <div className={styles.field}>
          <label className={styles.label}>{MODAL_CONTENT.typeLabel}</label>
          <div className={styles.typeChips}>
            {FEEDBACK_TYPE_OPTIONS.map((feedbackType) => (
              <button
                key={feedbackType.value}
                type="button"
                className={`${styles.chip} ${selectedType === feedbackType.value ? styles.chipActive : ''}`}
                onClick={() => setValue('type', feedbackType.value, { shouldValidate: true })}
              >
                {feedbackType.label}
              </button>
            ))}
          </div>
          {errors.type && <span className={styles.error}>{errors.type.message}</span>}
        </div>

        {selectedType && (
          <div className={styles.helperText}>{FEEDBACK_TYPE_HELPER_TEXT[selectedType]}</div>
        )}

        <div className={styles.field}>
          <label className={styles.label}>{MODAL_CONTENT.descriptionLabel}</label>
          <textarea
            {...register('description')}
            className={styles.textarea}
            rows={5}
            maxLength={1000}
          />
          <div className={styles.charCount}>
            {MODAL_CONTENT.characterCountFormat(description.length)}
          </div>
          {errors.description && (
            <span className={styles.error}>{errors.description.message}</span>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>{MODAL_CONTENT.currentPageLabel}</label>
          <Tooltip title={currentRoute} placement="top" arrow>
            <span className={styles.readOnlyInputWrapper}>
              <input
                type="text"
                readOnly
                value={currentRoute}
                className={styles.readOnlyInput}
                aria-label={MODAL_CONTENT.currentPageLabel}
              />
            </span>
          </Tooltip>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default FeedbackModal;
